var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import IntlMessageFormat from "intl-messageformat";
import { formatLocaleString } from "./locale";
import { icuDistanceUnits } from "./icuDistanceUnits";
import { TranslateTags } from "./translateTags";
import get from "just-safe-get";
import clone from "just-clone";
var DEFAULT_LOCALE = "en-GB";
var I18n = /** @class */ (function () {
    function I18n(options) {
        if (options === void 0) { options = {}; }
        var _this = this;
        var _a;
        this.intlExplorerWindow = null;
        this.intlExplorerReady = false;
        this.intlExplorerQueue = [];
        this.locale = formatLocaleString((_a = options.locale) !== null && _a !== void 0 ? _a : DEFAULT_LOCALE);
        this.mode = I18n.MODE.DEVELOPMENT;
        this.formatHelpers = [];
        this.intlData = {
            messages: {},
            formats: {},
        };
        this.createFormattedMessage = options.messageFormatter || this.messageFormatter;
        this.logError = options.logError || (function (err) { return console.error(err); });
        if (options.explorer) {
            this.intlExplorerWindow = window.open("https://intlexplorer.geome-test2.geoapp.me/", "_blank", "popup,width=620,height=600");
            if (!this.intlExplorerWindow) {
                console.error("Unable to open Intl Explorer window");
            }
            else {
                window.setTimeout(function () {
                    _this.intlExplorerReady = true;
                    _this.intlExplorerQueue.forEach(function (message) {
                        var _a;
                        (_a = _this.intlExplorerWindow) === null || _a === void 0 ? void 0 : _a.postMessage(message, "*");
                    });
                }, 3e3);
                window.addEventListener("beforeunload", function () { var _a; return (_a = _this.intlExplorerWindow) === null || _a === void 0 ? void 0 : _a.close(); });
            }
        }
    }
    I18n.prototype.setLocale = function (locale) {
        this.locale = formatLocaleString(locale);
    };
    I18n.prototype.setMode = function (mode) {
        if (!Object.values(I18n.MODE).includes(mode))
            throw new Error("Unrecognised Intl mode: ".concat(mode));
        this.mode = mode;
    };
    I18n.prototype.setIntlData = function (intlData) {
        if (intlData) {
            if (this.intlExplorerWindow) {
                this.sendToIntlExplorer({ type: "intl-messages", messages: intlData.messages });
            }
            this.intlData = {
                messages: intlData.messages,
                formats: intlData.formats,
            };
        }
    };
    I18n.prototype.setLocalisedIntlData = function (intlData) {
        this.setIntlData(intlData);
    };
    I18n.prototype.setRefreshFn = function (func) {
        this.refreshFn = func;
    };
    I18n.prototype.refresh = function () {
        if (!this.refreshFn)
            throw Error("Missing Refresh Function");
        this.setIntlData(this.refreshFn());
    };
    I18n.prototype.registerFormatHelper = function (helper) {
        return this.formatHelpers.push(helper);
    };
    I18n.prototype.formatMessage = function (message, params) {
        var _this = this;
        var _a;
        if (!this.intlData)
            throw new Error("No intlData");
        var helpedParams = this.formatHelpers.reduce(function (prevParams, helper) { return helper(_this, prevParams); }, params || {});
        var formats = this.intlData.formats;
        var mergedFormats = __assign(__assign({}, formats), ((_a = params === null || params === void 0 ? void 0 : params.formats) !== null && _a !== void 0 ? _a : {}));
        var normalisedFormats = this.normaliseFormats(mergedFormats);
        var formattedMsg = this.createFormattedMessage(message, [this.locale], normalisedFormats);
        var rawParams = Object.fromEntries(Object.entries(helpedParams).filter(function (_a) {
            var key = _a[0], _ = _a[1];
            return !["convert", "formats"].includes(key);
        }));
        return formattedMsg.format(rawParams);
    };
    I18n.prototype.t = function (messagePath, params) {
        if (this.intlExplorerWindow) {
            this.sendToIntlExplorer({ type: "intl-usage", token: messagePath });
        }
        if (this.mode === I18n.MODE.KEY)
            return messagePath;
        var message;
        if (params == null)
            params = {};
        if (!(message = this.getMessage(messagePath)))
            return this.noSuchMessage(messagePath, params);
        try {
            return this.formatMessage(message, __assign(__assign({}, params), TranslateTags));
        }
        catch (err) {
            console.error("Error with `".concat(messagePath, "`:\"").concat(message, "\", ").concat(JSON.stringify(params), ":"));
            throw err;
        }
    };
    I18n.prototype.f = function (formatPath) {
        var format;
        if (!(format = this.getFormat(formatPath))) {
            throw this.noSuchFormat(formatPath);
        }
        return format;
    };
    I18n.prototype.getMessage = function (path) {
        if (!this.intlData)
            throw new Error("No intlData");
        return get(this.intlData.messages, path);
    };
    I18n.prototype.getFormat = function (path) {
        if (!this.intlData)
            throw new Error("No intlData");
        if (!this.intlData.formats)
            throw new Error("No intlData formats");
        return get(this.intlData.formats, path);
    };
    // private
    I18n.prototype.sendToIntlExplorer = function (message) {
        var _a;
        if (!this.intlExplorerReady) {
            this.intlExplorerQueue.push(message);
            return;
        }
        (_a = this.intlExplorerWindow) === null || _a === void 0 ? void 0 : _a.postMessage(message, "*");
    };
    I18n.prototype.messageFormatter = function (message, locales, formats) {
        return new IntlMessageFormat(message, locales, formats);
    };
    I18n.prototype.normaliseFormats = function (formats) {
        var _a, _b;
        var normalisedFormats = clone(formats);
        var distanceUnit = null;
        if ((distanceUnit = (_b = (_a = normalisedFormats === null || normalisedFormats === void 0 ? void 0 : normalisedFormats.number) === null || _a === void 0 ? void 0 : _a.distance) === null || _b === void 0 ? void 0 : _b.unit)) {
            normalisedFormats.number.distance.unit = icuDistanceUnits(distanceUnit);
        }
        return normalisedFormats;
    };
    I18n.prototype.noSuchMessage = function (path, params) {
        var msg = "Missing translation: ".concat(path);
        switch (this.mode) {
            case I18n.MODE.STUBBED:
                return [path, JSON.stringify(params)].filter(Boolean).join(":");
            case I18n.MODE.PRODUCTION:
                this.logError(new Error("Missing Translation"), {
                    event: "missing-translation",
                    component: this,
                    key: path,
                    locale: this.locale,
                });
                return this.defaultMessage(path);
            case I18n.MODE.TEST:
                console.error(msg);
                return msg;
            default:
                throw new Error(msg);
        }
    };
    I18n.prototype.defaultMessage = function (path) {
        var tokens = path.split(".");
        var key = tokens[tokens.length - 1];
        return key.replace(/_/g, " ").replace(/\b\w/, function (l) { return l.toUpperCase(); });
    };
    I18n.prototype.noSuchFormat = function (path) {
        throw new Error("Missing format: ".concat(path));
    };
    I18n.MODE = {
        DEVELOPMENT: "development",
        PRODUCTION: "production",
        TEST: "test",
        STUBBED: "stubbed",
        KEY: "key",
    };
    return I18n;
}());
export default I18n;
